import { useWireValue } from '@forminator/react-wire'
import { useMemo, useState } from 'react'
import * as store from '@store'
import * as actions from '@actions'

const useUpsertTractViewModel = (
    showHideAdd, 
    editTract, 
    onSuccess, 
    shouldAssign = false,
    runsheetId = null,
    runsheetTabId = null,
    ownershipType = null,
) => {
    
    const nameAndIdToOption = ({ id, name }, list) => ({
        label: name,
        value: id,
        list,
    })
    
    const getUpsertTractInitialValues = editTract => {
        
        const currentDate = new Date().toDateString()
        
        return {
            id: editTract?.id || undefined,
            name: editTract?.name || '',
            grossAcreage: editTract?.grossAcreage || 0,
            lot: editTract?.lot || '',
            status: editTract?.status || '',
            statusDate: editTract?.statusDate || currentDate,
            certDate: editTract?.certDate || currentDate,
            countyId: editTract?.county?.id || null,
            state: editTract?.county?.state.id || null,
            survey: editTract?.survey || '',
            range: editTract?.range || '',
            township: editTract?.township || '',
            section: editTract?.section || '',
            abstract: editTract?.abstract || '',
            runsheetId: editTract?.runsheet?.id || null,
            runsheetTabId: editTract?.runsheetTab?.id || null,
            block: editTract?.block || '',
            section1: editTract?.section1 || '',
            section2: editTract?.section2 || '',
            section3: editTract?.section3 || '',
            section4: editTract?.section4 || '',
            subdivision: editTract?.subdivision || '',
            platBook: editTract?.platBook || '',
            platVolume: editTract?.platVolume || '',
            platPage: editTract?.platPage || '',
            description: editTract?.description || '',
        }
        
    }
    
    const states = useWireValue(store.states)
    
    const [values, setValues] = useState(getUpsertTractInitialValues(editTract))
    const [message, setMessage] = useState()
    const [isValid, setIsValid] = useState(true)
    
    const optionMaps = useMemo(() => {
        const maps = { states: {}, counties: {}, surveys: {}, blocks: {}, townships: {}, sections: {}, abstracts: {} }
        const keys = ['states', 'counties', 'surveys', 'blocks', 'townships', 'sections', 'abstracts']
        
        const buildObj = (idx, list) => {
            list?.forEach(v => {
                const currKey = keys[idx]
                const nextKey = keys[idx + 1]
                
                maps[currKey][v.id] = nameAndIdToOption(v, v[nextKey])
                buildObj(idx + 1, v[nextKey])
            })
        }
        
        buildObj(0, states)
        
        return maps
    }, [states])
    
    const getSelectboxValue = (key, value) => optionMaps[key]?.[value] || { value: '', label: '' }
    
    const getSelectboxOptions = (key, list) => list?.map(l => optionMaps[key][l.id]) || []
    
    const handleChange = e => {
        const key = e.target.name
        const value = e.target.value
        
        setValues(prev => ({ ...prev, [key]: value }))
    }
    
    const handleSelectboxChange = (name, value) => handleChange({ target: { name, value: value.value } })
    
    const handleSubmit = async e => {
        e.preventDefault()
        
        if (!values.name || !values.state || !values.countyId) {
            setMessage({ text: 'Please fill in all the required fields', level: 'danger' })
            setIsValid(false)
            return
        }
        
        setIsValid(true)
        
        if (!values.id) {
            await actions.createTract(
                values,
                shouldAssign,
                runsheetId,
                runsheetTabId,
                ownershipType,
            )
        } else {
            await actions.updateTract(
                values.id,
                values,
            )
        }
        
        showHideAdd(false)
        onSuccess?.()
        
    }
    
    return {
        values,
        optionMaps,
        nameAndIdToOption,
        getSelectboxValue,
        getSelectboxOptions,
        handleChange,
        handleSelectboxChange,
        handleSubmit,
        
        message,
        setMessage,
        
        isValid,
    }
    
}

export default useUpsertTractViewModel
